import React, { FC, useState } from 'react';

import { Button, Checkbox, Group, Space, Stack, Text } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';

import { ModalDescription } from 'components/modals/ModalDescription';
import { ModalTitle } from 'components/modals/ModalTitle';
import { useTeams } from 'hooks/use-teams';
import { RouterService } from 'services/RouterService';
import { TeamService } from 'services/TeamService';
import { UserService } from 'services/UserService';

import cx from './styles.module.scss';

type Props = ContextModalProps<{
    create: {
        title: string;
        description?: React.ReactNode;
    };
    switchy: {
        title: string;
        description?: React.ReactNode;
    };
    force: boolean;
}>;

const SwitchTeamModal: FC<Props> = ({ context, id, innerProps }) => {
    const { switchy, create, force } = innerProps;

    const [switching, setSwitching] = useState(false);
    const [selected, select] = useState<string | null>(null);

    const { teams } = useTeams();
    const all = teams.filter((team) => team.permissions.length > 0);

    const { title, description } = all.length === 0 ? create : switchy;

    const handleSwitch = async () => {
        if (selected) {
            setSwitching(true);

            await UserService.switchTeam(selected);
            RouterService.reload();

            context.closeContextModal(id);
        }
    };

    const handleCreate = async () => {
        context.closeContextModal(id);

        TeamService.openCreateTeam();
    };

    const cancel = async () => {
        if (force) {
            await RouterService.push('/');
        }

        context.closeContextModal(id);
    };

    return (
        <Stack gap="xs">
            <Space h="xs" />
            <ModalTitle>{title}</ModalTitle>
            <ModalDescription ta="center">{description}</ModalDescription>
            <div>
                <Stack gap="xs">
                    {all.map((team) => (
                        <Checkbox.Card
                            className={cx.card}
                            radius="sm"
                            onClick={() => {
                                select(team.id);
                            }}
                            checked={team.id === selected}
                            key={team.id}
                        >
                            <Group wrap="nowrap" align="flex-start">
                                <Checkbox.Indicator />
                                <Text className={cx.label}>{team.name}</Text>
                            </Group>
                        </Checkbox.Card>
                    ))}

                    {all.length > 0 ? (
                        <Button onClick={handleSwitch} fullWidth disabled={!selected} loading={switching}>
                            Switch team
                        </Button>
                    ) : (
                        <Button onClick={handleCreate} variant="gradient" fullWidth>
                            Create new team
                        </Button>
                    )}

                    <Button onClick={cancel} variant="subtle" fullWidth>
                        Cancel
                    </Button>
                </Stack>
            </div>
        </Stack>
    );
};

export { SwitchTeamModal };
